import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { API_ENDPOINTS } from '../../../config/api.config';
import { postData } from '../../../utils/apiUtils';
import snapgradeLogo from '../../../img/snapgrade-logo.png';
import './MagicLinkVerify.css';

const MagicLinkVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyMagicLink = async () => {
      try {
        // Extract token from URL query parameters
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');

        if (!token) {
          setVerificationStatus('error');
          setErrorMessage('Invalid or missing token. Please request a new magic link.');
          return;
        }

        // Call the API to verify the token
        const result = await postData(API_ENDPOINTS.MAGIC_LINK.VERIFY, { token });

        if (result.success) {
          // Store user data in local storage
          const userData = {
            name: result.name,
            role: result.role,
            userId: result.userId,
            school: result.school
          };
          
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('token', result.token);
          localStorage.setItem('refreshToken', result.refreshToken);
          
          // Identify user in PostHog
          if (posthog) {
            posthog.identify(userData.userId, {
              name: userData.name,
              role: userData.role,
              school: userData.school?.name,
              loginMethod: 'magic-link'
            });
          }
          
          // Trigger storage event for other components
          window.dispatchEvent(new Event('storage'));
          
          // Set verification status to success
          setVerificationStatus('success');
          
          // Automatically redirect after 2 seconds
          setTimeout(() => {
            // Navigate based on role
            switch(result.role.toLowerCase()) {
              case 'admin':
                navigate('/admin-dashboard');
                break;
              default:
                navigate('/dashboard');
            }
          }, 2000);
        } else {
          throw new Error(result.message || 'Verification failed');
        }
      } catch (error) {
        console.error('Magic link verification error:', error);
        setVerificationStatus('error');
        
        // Determine appropriate error message
        if (error.message?.includes('expired')) {
          setErrorMessage('This magic link has expired. Please request a new one.');
        } else if (error.message?.includes('already used')) {
          setErrorMessage('This magic link has already been used. Please request a new one.');
        } else {
          setErrorMessage('There was an error verifying your magic link. Please try again or request a new one.');
        }
      }
    };

    verifyMagicLink();
  }, [location.search, navigate]);

  const handleRequestNewLink = () => {
    navigate('/login', { state: { error: errorMessage } });
  };

  return (
    <div className="magic-verify-container">
      <div className="magic-verify-box">
        <img src={snapgradeLogo} alt="SnapGrade Logo" className="verify-logo" />
        
        {verificationStatus === 'verifying' && (
          <div className="verify-status">
            <div className="verify-spinner"></div>
            <h2>Verifying Magic Link</h2>
            <p>Please wait while we authenticate you...</p>
          </div>
        )}
        
        {verificationStatus === 'success' && (
          <div className="verify-status success">
            <div className="verify-icon success-icon">✓</div>
            <h2>Success!</h2>
            <p>You have been authenticated. Redirecting to your dashboard...</p>
          </div>
        )}
        
        {verificationStatus === 'error' && (
          <div className="verify-status error">
            <div className="verify-icon error-icon">✗</div>
            <h2>Authentication Failed</h2>
            <p>{errorMessage}</p>
            <button onClick={handleRequestNewLink} className="request-new-link">
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicLinkVerify; 